import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  addBrand,
  deleteBrand,
  updateBrand,
  fetchCategories,
  getBrands,
} from "../Services/inventoryService";
import Footer from "../Footer/Footer";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import IconButton from "@mui/material/IconButton";
import { Tooltip } from "@mui/material";

type OptionType = {
  id: string;
  label: string;
};

const BrandManagement: React.FC = () => {
  const [categories, setCategories] = useState<OptionType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [brands, setBrands] = useState<any[]>([]);
  const [newBrand, setNewBrand] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editBrand, setEditBrand] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const orgId = localStorage.getItem("orgId") || "";
  // Fetch Categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchCategories(orgId);
        const formatted = response.map(
          (cat: { categoryId: number; categoryName: string }) => ({
            id: cat.categoryId,
            label: cat.categoryName,
          })
        );
        setCategories(formatted);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    loadCategories();
  }, []);
  // Fetch Brands when Category is Selected
  useEffect(() => {
    const loadBrands = async () => {
      if (!selectedCategory) return;
      try {
        const response = await getBrands(orgId, selectedCategory);
        // const formatted = response.map((brand: { brandId: number; brandName: string }) => ({
        //   id: brand.brandId,
        //   name: brand.brandName,
        // }));
        const formatted = response.map((brand: any) => ({
          id: brand.brandId,
          name: brand.brandName,
          status: brand.status || "active",
          createdBy: brand.createdBy || "N/A",
          createdDate: new Date(brand.createdDate).toLocaleString(),
          updatedBy: brand.updatedBy || "N/A",
          updatedDate: new Date(brand.updatedDate).toLocaleString(),
        }));
        setBrands(formatted);
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    };
    loadBrands();
  }, [selectedCategory]);

  // Add New Brand
  // const handleAddBrand = async () => {
  //   if (!newBrand.trim()) {
  //     alert("Please enter a brand name.");
  //     return;
  //   }
  //   try {
  //     const createdBy = "adminUser";
  //     const response = await addBrand(selectedCategory, newBrand, orgId, createdBy);
  //     if (response.success) {
  //       setBrands([...brands, { id: response.brandId, name: newBrand }]);
  //       setNewBrand("");
  //     } else {
  //       alert("Failed to add brand.");
  //     }
  //   } catch (error) {
  //     console.error("Error adding brand:", error);
  //   }
  // };
  const handleAddBrand = async () => {
    if (!newBrand.trim()) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter a brand name.",
      });
      return;
    }
    try {
      const createdBy = "adminUser";
      const response = await addBrand(
        selectedCategory,
        newBrand,
        orgId,
        createdBy
      );

      if (response.success) {
        const newBrandData = {
          id: response.brandId,
          name: newBrand,
          createdBy,
          createdDate: new Date().toLocaleString(),
          updatedBy: createdBy,
          updatedDate: new Date().toLocaleString(),
          status: "active",
        };

        setBrands([...brands, newBrandData]);
        setNewBrand("");
        Swal.fire({
          icon: "success",
          title: "Brand Added",
          text: "Brand has been added.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add brand.",
        });
      }
    } catch (error) {
      console.error("Error adding brand:", error);
    }
  };

  // Handle Edit Brand
  const handleEditBrand = async () => {
    if (!editBrand) return;

    try {
      const response = await updateBrand(
        editBrand.id,
        editBrand.name,
        orgId,
        "adminUser"
      );
      if (response.success) {
        setBrands(
          brands.map((b) =>
            b.id === editBrand.id ? { ...b, name: editBrand.name } : b
          )
        );
        setOpenDialog(false);
        Swal.fire({
          icon: "success",
          title: "Brand Updated",
          text: "Brand has been updated successfully.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed to update brand",
          text: response.message,
        });
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      Swal.fire({
        icon: "error",
        title: "Error updating brand",
        text: "Something went wrong. Please try again.",
      });
    }
  };

  // Handle Delete Brand
  const handleDeleteBrand = async (brandId: string) => {
    const { isConfirmed } = await Swal.fire({
      title: "Delete Brand?",
      text: "Are you sure you want to delete this brand?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!isConfirmed) return;

    try {
      const response = await deleteBrand(brandId, orgId, "adminUser");
      if (response.success) {
        setBrands(brands.filter((b) => b.id !== brandId));
        Swal.fire("Deleted!", "Brand has been deleted.", "success");
      } else {
        Swal.fire("Error", "Failed to delete brand.", "error");
      }
    } catch (error) {
      console.error("Error deleting brand:", error);
      Swal.fire("Error", "Error deleting brand.", "error");
    }
  };

  // DataGrid Columns
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Brand Name", width: 200 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "createdBy", headerName: "Created By", width: 150 },
    { field: "createdDate", headerName: "Created Date", width: 200 },
    { field: "updatedBy", headerName: "Updated By", width: 150 },
    { field: "updatedDate", headerName: "Updated Date", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <>
         <Tooltip title="Edit">
         <IconButton size="small" sx={{ padding: 0.5}}
            onClick={() => {
              setEditBrand({ id: params.row.id, name: params.row.name });
              setOpenDialog(true);
            }} 
          ><EditIcon fontSize="small"  />
          </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
          <IconButton size="small" sx={{ padding: 0 }}
            color="error"
            onClick={() => handleDeleteBrand(params.row.id)}           
          ><DeleteIcon  fontSize="small"/>
          </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];
  return (
    <>
      <div className="page-location">
        <span>Master &gt; Brand Master</span>
      </div>
      <Container maxWidth="lg" sx={{ padding: 1 }}>
        <Box sx={{ padding: 4 }}>
          {/* <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "black",
              textTransform: "uppercase",
              textAlign: "center",
              marginBottom: 2,
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            }}
          >
            Brand Management
          </Typography> */}

          {/* Select Category */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              marginBottom: 6,
            }}
          >
            <FormControl
              sx={{ width: "50%", marginBottom: 2 }}
              variant="outlined"
            >
              <InputLabel id="select-category-label">
                Select Category
              </InputLabel>
              <Select
                labelId="select-category-label"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Select Category"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCategory && (
              <TextField
                label="New Brand"
                variant="outlined"
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                required
                error={newBrand.trim() === ""}
                helperText={newBrand.trim() === "" ? "" : ""}
                sx={{ width: "50%" }}
              />
            )}
          </Box>
          {selectedCategory && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: 2,
              }}
            >
              <Button
                variant="contained"
                //startIcon={<AddIcon />}
                onClick={handleAddBrand}
                sx={{ height: 40, marginBottom: 5 }}
              >
                {/* Add Brand */}
                Submit
              </Button>
            </Box>
          )}

          {/* MUI DataGrid */}
          <DataGrid
            rows={brands}
            columns={columns}
            pageSizeOptions={[5]}
            autoHeight
          />

          {/* Edit Brand Dialog */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>Edit Brand</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Brand Name"
                value={editBrand?.name || ""}
                onChange={(e) =>
                  setEditBrand((prev) =>
                    prev ? { ...prev, name: e.target.value } : null
                  )
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleEditBrand}>
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
      <Box
        sx={{
          position: "relative",
          left: 0,
          bottom: 0,
          width: "100%",
          mt: 3,
        }}
      >
        <Footer />
      </Box>
    </>
  );
};
export default BrandManagement;
